import { ApiActions } from '@orientaction/api-actions';
import { useAlert, useLanguage, useOAMatch, useProfileMenu } from '@orientaction/hooks';
import { userService } from '@orientaction/services';
import { getFromLS, isAdmin, isRootUser, OA_MATCH_SERVICE_IDS, updateUserInfoInLS, userHasAllowedService, userHasAnyService } from '@orientaction/utils';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { apiServiceNative } from '../../../../../utils/fetchNative';

export const useAppHeader = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const alert = useAlert();
  const { language, onLanguageChange } = useLanguage();
  const user = JSON.parse(getFromLS('userInfos') || '{}');
  const { open, anchorRef, handleToggle, handleClose, goToProfileEdit, logout } = useProfileMenu({
    user,
  });

  const mainLink = isAdmin(user) ? '/admin/users-resultats' : '/main';
  const activateMatch = getFromLS('activateMatch') === 'true';
  const disableMatch = process.env.REACT_APP_DISABLE_MATCH === 'true' && !activateMatch;

  const redirectUser = () => {
    dispatch(ApiActions.setTabLayout(0 as any));
    history.push(user ? mainLink : '/');
  };

  const switchRole = async () => {
    try {
      const isSecondRoleActivated = user?.company_user_role?.id === 4;
      const token = getFromLS('userToken') || '';
      const { data } = await userService.switchRole(token, !isSecondRoleActivated);
      localStorage.setItem('userInfos', JSON.stringify(data));
      const redirectPath = data?.company_user_role?.id === 5 ? '/main' : '/admin/users-resultats';
      history.push(redirectPath);
    } catch (error) {
      alert.openSimpleAlert();
    }
  };

  const canAccessOaMatch = useMemo(
    () =>
      userHasAnyService(user, OA_MATCH_SERVICE_IDS) ||
      (!user.rattachement && user.role?.type !== 'admin') ||
      user?.company_user_role?.id === 4,
    [user],
  );

  const showRecruitmentSwitch = userHasAllowedService(user, 4);

  const { verifyUser, signupToOAMatch } = useOAMatch();
  const [openDialogOAMatch, setOpenDialogOAMatch] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [loadingPoppinsOAMatch, setLoadingPoppinsOAMatch] = useState(false);
  const [textPoppinOAMatch, setTextPoppinOAMatch] = useState({
    title: '',
    text: 'Voulez-vous rejoindre Match le réseau social qui s’adapte à votre personnalité ?',
    btnClose: 'Pas maintenant',
    btnAccess: 'Rejoindre',
  });

  const urlOAMatch = useCallback(() => {
    const token = getFromLS('userToken') || '';
    return `${process.env.REACT_APP_OA_MATCH_URL}/connexion/#token=${token}`;
  }, []);

  const updateViewOAMatchModal = async (status: string) => {
    try {
      const token = getFromLS('userToken') || '';
      updateUserInfoInLS({ statusOAMatchModal: status });
      await userService.modifyUser({
        token,
        statusOAMatchModal: status,
        id: user?.id,
      });
    } catch (error) {
      console.log({ error });
    }
  };

  const addSubNetwork = async (idUser: number) => {
    try {
      await apiServiceNative.get(
        `/api/create-update-value-personality-subnetwork?userId=${idUser}`,
      );
      await apiServiceNative.get(
        `/api/create-update-value-meaning-subnetwork?userId=${idUser}`,
      );
    } catch (error) {
      console.error(error);
    }
  };

  const checkOAMatchIfUserHasRefused = () => {
    if (user.statusOAMatchModal === 'refused') {
      setOpenDialogOAMatch(true);
    } else {
      window.open(urlOAMatch(), '_blank');
    }
  };

  const closeModalOA = () => {
    setOpenDialogOAMatch(false);
    updateViewOAMatchModal('refused');
  };

  const closeAndRedirect = () => {
    window.open(urlOAMatch(), '_blank');
    setOpenDialogOAMatch(false);
  };

  const redirectToOAMatch = async () => {
    setLoadingPoppinsOAMatch(true);
    try {
      const userExist = await verifyUser(user?.id?.toString());
      if (userExist) {
        await addSubNetwork(user.id);
        window.open(urlOAMatch(), '_blank');
        updateViewOAMatchModal('accepted');
        setLoadingPoppinsOAMatch(false);
        setOpenDialogOAMatch(false);
        return;
      }
      const resp = await signupToOAMatch(user);
      if (resp.data.addExternUser.code === 200) {
        await addSubNetwork(user.id);
        setLoadingPoppinsOAMatch(false);
        setTextPoppinOAMatch({
          title: 'Votre compte est maintenant lié à Match',
          text: 'Vous pouvez dès à présent vous connecter avec vos identifiants sur la page de connexion de Match.',
          btnClose: 'Fermer',
          btnAccess: 'Se connecter à Match',
        });
        updateViewOAMatchModal('accepted');
      } else {
        setTextPoppinOAMatch(prev => ({
          ...prev,
          title: 'Un problème est survenu',
          text: 'Veuillez retentez plus tard en passant par les boutons Match.',
        }));
      }
    } catch (error) {
      setTextPoppinOAMatch(prev => ({
        ...prev,
        title: 'Un problème est survenu',
        text: 'Veuillez retentez plus tard en passant par les boutons Match.',
      }));
      setLoadingPoppinsOAMatch(false);
      console.log(error);
    }
  };

  return {
    user,
    language,
    onLanguageChange,
    open,
    anchorRef,
    handleToggle,
    handleClose,
    goToProfileEdit,
    logout,
    redirectUser,
    switchRole,
    canAccessOaMatch,
    disableMatch,
    showRecruitmentSwitch,
    isAdminUser: isAdmin(user),
    isRootUser: isRootUser(user),
    openDialogOAMatch,
    setOpenDialogOAMatch,
    loadingPoppinsOAMatch,
    textPoppinOAMatch,
    checkOAMatchIfUserHasRefused,
    closeModalOA,
    closeAndRedirect,
    redirectToOAMatch,
    mobileNavOpen,
    toggleMobileNav: () => setMobileNavOpen(prev => !prev),
    closeMobileNav: () => {
      setMobileNavOpen(false);
      setMobileProfileOpen(false);
    },
    mobileProfileOpen,
    toggleMobileProfile: () => setMobileProfileOpen(prev => !prev),
  };
};
